export interface LegalBlock {
  heading?: string;
  paragraphs?: string[];
  items?: string[];
}

export interface LegalSection {
  id: 'imprint' | 'privacy' | 'cookies' | 'credits';
  title: string;
  intro?: string;
  blocks: LegalBlock[];
}

export const legalUpdated = 'October 21, 2026';

/** Imprint details, shown as a label / value list */
export const imprintDetails = [
  { label: 'Company name', value: 'KAYS VENTURES FZCO' },
  { label: 'Registered office', value: 'Dubai Silicon Oasis, DDP, Building A1, Dubai, United Arab Emirates' },
  { label: 'Phone', value: '00971 / 505254282', href: 'tel:+971505254282' },
  { label: 'Email', value: 'hey@superluxury.io', href: 'mailto:hey@superluxury.io' },
  { label: 'Website', value: 'superluxury.io', href: 'https://superluxury.io' },
  { label: 'Managing director', value: 'Adrian Kays' },
  { label: 'Trade license number', value: '16239' },
  { label: 'VAT identification number', value: 'Pending application' },
  { label: 'Regulatory authority', value: 'IFZA' },
];

/** 3D model credits */
export const modelCredits = [
  {
    work: 'Drone',
    workUrl: 'https://sketchfab.com/3d-models/drone-764eea3376134998bd49bb8e5f7d795e',
    author: 'FeelsBadMan',
    authorUrl: 'https://sketchfab.com/FeelsBadMan',
    license: 'CC BY 4.0',
    licenseUrl: 'https://creativecommons.org/licenses/by/4.0/',
    note: 'Used as the base model for Pixel, our on-site companion. Modified: new face display, materials, animation and rigging.',
  },
];

export const legalSections: LegalSection[] = [
  {
    id: 'imprint',
    title: 'Imprint',
    intro: 'Laebel is a brand operated by KAYS VENTURES FZCO. Information according to applicable disclosure requirements:',
    blocks: [
      {
        heading: 'Responsibility for content',
        paragraphs: [
          'We create the content of this website with great care. However, we cannot guarantee that it is complete, accurate or up to date at all times. As a service provider, we are responsible for our own content on these pages under general law.',
        ],
      },
      {
        heading: 'Links to other websites',
        paragraphs: [
          'Our website contains links to external websites over whose content we have no control. The respective provider or operator is always responsible for the content of linked pages. If we become aware of any legal violations, we will remove such links immediately.',
        ],
      },
      {
        heading: 'Copyright',
        paragraphs: [
          'All content, designs, images, videos and texts on this website are the property of KAYS VENTURES FZCO or its clients and partners, unless stated otherwise. Reproduction, editing or distribution outside the limits of copyright law requires our prior written consent. Third-party content is marked as such — see Credits below.',
        ],
      },
      {
        heading: 'Website credits',
        paragraphs: [
          'Design, Creative Direction and Development by Julius Branding.',
          '100% made with AI in Germany.',
        ],
      },
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy Policy',
    intro:
      'This policy explains what personal data we process when you visit this website or get in touch with us, why we process it, and which rights you have. We process personal data in line with the UAE Personal Data Protection Law (Federal Decree-Law No. 45 of 2021) and, where it applies to visitors from the European Union, the General Data Protection Regulation (GDPR).',
    blocks: [
      {
        heading: '1. Who is responsible',
        paragraphs: [
          'KAYS VENTURES FZCO, Dubai Silicon Oasis, DDP, Building A1, Dubai, United Arab Emirates. Email: hey@superluxury.io. For any privacy request, write to us at this address.',
        ],
      },
      {
        heading: '2. Data we process when you visit',
        paragraphs: [
          'When you open this website, your browser automatically sends technical information to the servers that deliver it. This includes:',
        ],
        items: [
          'IP address and approximate location derived from it',
          'Date and time of the request',
          'Browser type, version and operating system',
          'The page you visited and the referring website',
        ],
      },
      {
        paragraphs: [
          'We need this data to deliver the website securely and reliably, and to detect and prevent misuse. The legal basis is our legitimate interest in operating a secure website (Art. 6(1)(f) GDPR). Server logs are deleted after a short period, typically within 30 days, unless they are needed to investigate a security incident.',
        ],
      },
      {
        heading: '3. Contacting us',
        paragraphs: [
          'If you email us or use one of the contact links on this site, we process the information you send — usually your name, email address, company and the content of your message — to answer your request and, where relevant, prepare a project. The legal basis is the performance of pre-contractual measures (Art. 6(1)(b) GDPR) or our legitimate interest in responding to enquiries. We delete this data once it is no longer needed, unless statutory retention periods require us to keep it longer.',
        ],
      },
      {
        heading: '4. Chatting with Pixel',
        paragraphs: [
          'Pixel, the robot on this site, runs entirely in your browser. His conversations are pre-written; nothing you click is sent to us or to an AI service, and no chat history is stored.',
        ],
      },
      {
        heading: '5. Third-party services',
        paragraphs: [
          'To display this website, your browser loads some files from external providers. In doing so, your IP address is transmitted to them. We use:',
        ],
        items: [
          'Google Fonts and Google static content (Google Ireland Limited / Google LLC) — for web fonts and the 3D model decoder',
          'Vercel (Vercel Inc.) — for hosting media and 3D files',
          'Content delivery networks — for images and videos',
        ],
      },
      {
        paragraphs: [
          'Some of these providers may process data outside the UAE or the EU, including in the United States. Where required, transfers are protected by appropriate safeguards such as the EU Standard Contractual Clauses.',
        ],
      },
      {
        heading: '6. Social media links',
        paragraphs: [
          'Links to Instagram, LinkedIn and X are plain links. No data is sent to these platforms until you click a link and leave our website; from then on, the respective platform’s privacy policy applies.',
        ],
      },
      {
        heading: '7. Your rights',
        paragraphs: ['Subject to the applicable law, you have the right to:'],
        items: [
          'Access the personal data we hold about you',
          'Have inaccurate data corrected',
          'Have your data deleted or its processing restricted',
          'Object to processing based on our legitimate interests',
          'Receive your data in a portable format',
          'Withdraw any consent you have given, with effect for the future',
          'Lodge a complaint with a data protection supervisory authority',
        ],
      },
      {
        paragraphs: ['To exercise your rights, email hey@superluxury.io. We will respond within the time limits set by law.'],
      },
      {
        heading: '8. Security',
        paragraphs: [
          'This website is delivered over an encrypted connection (HTTPS). We take appropriate technical and organisational measures to protect your data against loss, misuse and unauthorised access.',
        ],
      },
      {
        heading: '9. Changes',
        paragraphs: [
          'We may update this policy when our website or the law changes. The current version is always available on this page.',
        ],
      },
    ],
  },
  {
    id: 'cookies',
    title: 'Cookies',
    intro:
      'Cookies are small text files stored on your device. Similar technologies, such as your browser’s local storage, work in a comparable way. Here is how we use them.',
    blocks: [
      {
        heading: 'What we use',
        paragraphs: [
          'This website currently uses only technically necessary storage — for example, to remember your settings within a single visit or to make animations and scrolling work smoothly. We do not use advertising cookies, and we do not track you across other websites.',
        ],
      },
      {
        heading: 'Analytics and marketing',
        paragraphs: [
          'We do not currently use analytics or marketing cookies. If we introduce them in the future, we will only set them after you have given your consent, and we will update this page beforehand.',
        ],
      },
      {
        heading: 'Third-party content',
        paragraphs: [
          'When files such as fonts, images or videos are loaded from external providers (see Privacy Policy, section 5), those providers may use technically necessary cookies or similar technologies to deliver them.',
        ],
      },
      {
        heading: 'Managing cookies',
        paragraphs: [
          'You can view, block or delete cookies at any time in your browser settings. Blocking technically necessary storage may affect how parts of the website work.',
        ],
      },
    ],
  },
  {
    id: 'credits',
    title: 'Credits',
    intro: 'Some elements of this website are based on third-party work. Thank you to the creators.',
    blocks: [],
  },
];
